import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEcho } from "@/lib/Echo/EchoProvider";
import { useOptionalUser } from "@/lib/Auth/useUser";

export type ThreadMember = {
  id: string;
  name: string | null;
  role: "ADMIN" | "SUPPLIER" | "DRIVER";
};

type MessageSentEvent = { thread_id: number; user_id: string };

const TYPING_SEND_INTERVAL_MS = 3_000;
const TYPING_EXPIRES_MS = 5_000;

export type ThreadPresence = {
  presentUserIds: Set<string>;
  typingMembers: ThreadMember[];
  notifyTyping: () => void;
};

export function useThreadPresenceChannel(threadId: string): ThreadPresence {
  const echo = useEcho();
  const me = useOptionalUser();
  const queryClient = useQueryClient();
  const [members, setMembers] = useState<ThreadMember[]>([]);
  const [typingMembers, setTypingMembers] = useState<ThreadMember[]>([]);
  const typingTimers = useRef(new Map<string, number>());
  const lastTypingSentAt = useRef(0);

  const channelName = `thread.${threadId}`;

  useEffect(() => {
    if (!echo) {
      return;
    }

    const timers = typingTimers.current;

    function stopTyping(userId: string) {
      window.clearTimeout(timers.get(userId));
      timers.delete(userId);
      setTypingMembers((current) => current.filter((m) => m.id !== userId));
    }

    echo
      .join(channelName)
      .here((here: ThreadMember[]) => setMembers(here))
      .joining((member: ThreadMember) =>
        setMembers((current) => [
          ...current.filter((m) => m.id !== member.id),
          member,
        ]),
      )
      .leaving((member: ThreadMember) => {
        setMembers((current) => current.filter((m) => m.id !== member.id));
        stopTyping(member.id);
      })
      .listenForWhisper("typing", (member: ThreadMember) => {
        window.clearTimeout(timers.get(member.id));
        timers.set(
          member.id,
          window.setTimeout(() => stopTyping(member.id), TYPING_EXPIRES_MS),
        );
        setTypingMembers((current) =>
          current.some((m) => m.id === member.id)
            ? current
            : [...current, member],
        );
      })
      .listen(".message.sent", (event: MessageSentEvent) => {
        stopTyping(event.user_id);
        queryClient.invalidateQueries({
          queryKey: ["ThreadMessages.Infinite"],
        });
      });

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
      echo.leave(channelName);
      setMembers([]);
      setTypingMembers([]);
    };
  }, [echo, channelName, queryClient]);

  function notifyTyping() {
    const self = members.find((m) => m.id === me?.id);
    const now = Date.now();

    if (
      !echo ||
      !self ||
      now - lastTypingSentAt.current < TYPING_SEND_INTERVAL_MS
    ) {
      return;
    }

    lastTypingSentAt.current = now;
    echo.join(channelName).whisper("typing", self);
  }

  return {
    presentUserIds: new Set(
      members.filter((m) => m.id !== me?.id).map((m) => m.id),
    ),
    typingMembers,
    notifyTyping,
  };
}
